import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import LegacyButton from '@atlaskit/button/standard-button';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Select, { CreatableSelect, PopupSelect } from '@atlaskit/select';
import TextField from '@atlaskit/textfield';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { EocForm, ErrorMessage, EocField as Field, FormFooter } from '@atlassian/eoc-form';
import PriorityBadge, { PRIORITIES, type Priority } from '@atlassian/eoc-priority-badge';

import { MAX_INTERVAL_TIME_YEARS } from '../../../common/constants';
import commonMessages from '../../../common/messages';
import type { ExpireUnitOption, FormData } from '../../../common/types';
import { useFormController } from '../../../controllers/form';
import { useHeartbeatsContext } from '../../../controllers/providers/heartbeats';

import { AssignedTeamInput } from './assigned-team-input';
import messages from './messages';
import { NameInput } from './name-input';
import type { Expire, Props } from './types';
import { getIntervalUnitOptions, isNumeric } from './utils';

const AlertPropertiesHeaderContainerStyles = xcss({
	marginTop: 'space.400',
});
const ExpectFieldSelectStyles = xcss({
	width: '120px',
});
const StringDescriptionStyles = xcss({
	marginBottom: 'space.300',
	color: 'color.text.subtle',
});

export const Form: React.FC<Props> = ({ data }) => {
	const { formatMessage } = useIntl();
	const { focusState } = useHeartbeatsContext();

	const { handleSubmit } = useFormController();

	const initialValueAlertMessage = useMemo(
		() => ({
			alertPriority: data?.alertPriority || 'P3',
			alertMessage: data?.alertMessage,
		}),
		[data?.alertMessage, data?.alertPriority],
	);

	return (
		<EocForm subscription={{ submitting: true }} onSubmit={handleSubmit}>
			{({ submitting }) => (
				<>
					<Box>
						<Heading size="large">
							{focusState.mode === 'edit'
								? formatMessage(messages.editFormHeader)
								: formatMessage(messages.createFormHeader)}
						</Heading>
					</Box>

					<NameInput initialValue={data?.name || ''} />

					<Field<FormData['description']>
						name="description"
						label={formatMessage(messages.descriptionLabel)}
						initialValue={data?.description}
					>
						{({ input }) => (
							<TextField
								autoComplete="off"
								placeholder={formatMessage(messages.descriptionPlaceholder)}
								{...input}
							/>
						)}
					</Field>

					<AssignedTeamInput ownerTeamId={data?.ownerTeamId} />

					<Field<Expire>
						aria-required={true}
						name="expect"
						label={formatMessage(messages.expectLabel)}
						isRequired
						initialValue={{
							interval: data?.interval !== undefined ? data.interval.toString() : '',
							unit: data?.intervalUnit
								? { label: data.intervalUnit, value: data.intervalUnit }
								: undefined,
						}}
						validate={(value) => {
							if (value.interval === '0') {
								return formatMessage(messages.invalidExpectErrorText);
							}
							if (!value.interval) {
								return formatMessage(messages.expectIntervalErrorText);
							}
							if (!value.unit) {
								return formatMessage(messages.expectUnitErrorText);
							}

							if (value.interval && value.unit) {
								// 5 years === 1826 days
								const limit = MAX_INTERVAL_TIME_YEARS * 365 + 1;
								const minuteLimit = limit * 24 * 60;
								const dayLimit = limit * 24;

								const unit = value.unit.value as ExpireUnitOption['value'];
								const interval = parseInt(value.interval as string, 10);

								if (unit === 'minutes' && interval > minuteLimit) {
									return formatMessage(messages.invalidInterval, {
										max: minuteLimit,
										unit,
									});
								}

								if (unit === 'hours' && interval > dayLimit) {
									return formatMessage(messages.invalidInterval, {
										max: dayLimit,
										unit,
									});
								}

								if (unit === 'days' && interval > limit) {
									return formatMessage(messages.invalidInterval, {
										max: limit,
										unit,
									});
								}
							}
						}}
					>
						{({ input, meta }) => (
							<>
								<Inline space="space.050">
									<TextField
										{...input}
										placeholder="1"
										value={input.value.interval}
										autoComplete="off"
										width={120}
										onChange={(e) => {
											const value = e.currentTarget.value;
											if (!isNumeric(value)) {
												return;
											}

											input.onChange({
												...input.value,
												interval: value,
											});
										}}
									/>
									<Box xcss={ExpectFieldSelectStyles}>
										<Select
											{...input}
											value={input.value.unit}
											options={getIntervalUnitOptions()}
											onChange={(value) => {
												input.onChange({
													...input.value,
													unit: value as ExpireUnitOption,
												});
											}}
										/>
									</Box>
								</Inline>
								{meta.touched && meta.error && <ErrorMessage>{meta.error}</ErrorMessage>}
							</>
						)}
					</Field>

					<Box xcss={AlertPropertiesHeaderContainerStyles}>
						<Heading size="medium">{formatMessage(messages.alertPropertiesTitle)}</Heading>
						<Box as="p" xcss={StringDescriptionStyles}>
							{formatMessage(messages.alertPropertiesDescription)}
						</Box>
					</Box>

					<Field<FormData['alertMessage']>
						aria-required={true}
						name="alertMessage"
						label={formatMessage(messages.alertMessageLabel)}
						isRequired
						initialValue={initialValueAlertMessage}
						validate={(value) => {
							if (!value?.alertMessage) {
								return 'error';
							}
						}}
					>
						{({ input, meta }) => {
							return (
								<>
									<TextField
										{...input}
										value={input.value.alertMessage}
										elemBeforeInput={
											<PopupSelect
												searchThreshold={undefined}
												options={Object.entries(PRIORITIES).map(([key, value]) => {
													return {
														value: key,
														label: value.label,
													};
												})}
												formatOptionLabel={(option) => {
													return (
														<Inline alignBlock="center" space="space.100">
															<PriorityBadge priority={option.value as Priority} />
															<Box>{option.label}</Box>
														</Inline>
													);
												}}
												target={({ isOpen, ...triggerProps }) => (
													<LegacyButton
														{...triggerProps}
														appearance="subtle"
														iconBefore={<PriorityBadge priority={input.value.alertPriority} />}
														iconAfter={
															<ChevronDownIcon
																color="currentColor"
																label={formatMessage(commonMessages.chevronDownIconLabel)}
															/>
														}
													/>
												)}
												onChange={(selection) => {
													input.onChange({
														...input.value,
														alertPriority: selection?.value as Priority,
													});

													input.onBlur();
												}}
											/>
										}
										onChange={(e) => {
											input.onChange({
												...input.value,
												alertMessage: e.currentTarget.value,
											});

											input.onBlur();
										}}
									/>
									{meta.touched && meta.error && (
										<ErrorMessage>{formatMessage(messages.alertMessageErrorText)}</ErrorMessage>
									)}
								</>
							);
						}}
					</Field>

					<Field<FormData['tags']>
						name="tags"
						label={formatMessage(messages.tagsLabel)}
						initialValue={
							data?.alertTags
								? data.alertTags.map((tag) => ({
										label: tag,
										value: tag,
									}))
								: []
						}
					>
						{({ input }) => (
							<CreatableSelect
								{...input}
								placeholder={formatMessage(messages.tagsPlaceholder)}
								isClearable
								isMulti
								noOptionsMessage={() => null}
								components={{
									DropdownIndicator: () => null,
								}}
							/>
						)}
					</Field>

					<FormFooter>
						<ButtonGroup>
							<Button
								appearance="subtle"
								onClick={(_, analyticsEvent) => {
									fireUIAnalytics(analyticsEvent, 'closeHeartbeatFormButton');

									focusState.close();
								}}
							>
								{formatMessage(commonMessages.cancel)}
							</Button>
							<Button type="submit" appearance="primary" isLoading={submitting}>
								{focusState.mode === 'edit'
									? formatMessage(messages.editHeartbeat)
									: formatMessage(messages.createHeartbeat)}
							</Button>
						</ButtonGroup>
					</FormFooter>
				</>
			)}
		</EocForm>
	);
};
